class UpsellVariantSelector extends HTMLElement {
  constructor() {
    super();

    this.modalElement = this.closest("x-drawer");
    this.variants = this.getProductVariants();
    this.referenceCheckboxId = this.getAttribute("reference-checkbox-id");
    this.optionNames = this.getAttribute("option-names");
    this.selectedVariant = null;
    this.moneyFormat = this.getAttribute("data-money-format");
    this.shopLocale = this.getAttribute("data-shop-locale");

    this.form = this.querySelector("form");
    if (this.form) {
      this.form.addEventListener("change", this.handleFormChange.bind(this));
    }

    if (this.modalElement) {
      this.saveButton = this.modalElement.querySelector(
        'button[aria-controls="upsell-modal-save-btn"]'
      );
      if (this.saveButton) {
        this.saveButton.addEventListener(
          "click",
          this.handleSaveButtonClick.bind(this)
        );
      }
    }

    this.preselectCurrentVariant();
  }

  getProductVariants() {
    try {
      const scriptElement = this.querySelector(
        'script[type="application/json"]'
      );
      if (!scriptElement) {
        return [];
      }
      const productVariants = scriptElement.textContent;
      return JSON.parse(productVariants);
    } catch (error) {
      return [];
    }
  }

  preselectCurrentVariant() {
    const referenceCheckbox = document.querySelector(
      `input[id="${this.referenceCheckboxId}"]`
    );

    let currentVariant = null;
    if (referenceCheckbox) {
      const currentVariantId = parseInt(referenceCheckbox.value, 10);
      currentVariant = this.variants.find((v) => v.id === currentVariantId);
    }
    if (!currentVariant) {
      currentVariant =
        this.variants.find((variant) => variant.available) || this.variants[0];
    }
    if (!currentVariant) return;
    this.selectedVariant = currentVariant;

    this.preselectOptionsSequentially(currentVariant);
  }

  async preselectOptionsSequentially(variant) {
    if (!this.form) return;

    for (let i = 1; i <= 3; i++) {
      const optionValue = variant[`option${i}`];
      if (!optionValue || optionValue === "Default Title") continue;

      const groupRadios = this.form.querySelectorAll(
        'input[name^="variant-group-"]'
      );
      if (groupRadios.length > 0 && i === 1) {
        let matchedGroupRadio = null;
        for (const groupRadio of groupRadios) {
          if (optionValue.includes(groupRadio.value)) {
            matchedGroupRadio = groupRadio;
            break;
          }
        }

        if (matchedGroupRadio) {
          matchedGroupRadio.checked = true;
          matchedGroupRadio.dispatchEvent(
            new Event("change", { bubbles: true })
          );

          // Wait for animations to complete
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      const radioInput = this.form.querySelector(
        `input[type="radio"][value="${optionValue}"]`
      );

      if (radioInput) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event("change", { bubbles: true }));

        const optionName = radioInput.name;
        const variantTitleValue = this.querySelector(
          `.variant-list-title-value[data-option-name="${optionName}"]`
        );
        if (variantTitleValue) {
          variantTitleValue.textContent = optionValue;
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));
        continue;
      }

      const selectElements = this.form.querySelectorAll("select");

      for (const selectElement of selectElements) {
        const option = Array.from(selectElement.options).find(
          (opt) => opt.value === optionValue
        );
        if (option) {
          selectElement.value = optionValue;
          selectElement.dispatchEvent(new Event("change", { bubbles: true }));

          const optionName = selectElement.name;
          const variantTitleValue = this.querySelector(
            `.variant-list-title-value[data-option-name="${optionName}"]`
          );
          if (variantTitleValue) {
            variantTitleValue.textContent = optionValue;
          }

          await new Promise((resolve) => requestAnimationFrame(resolve));
          break;
        }
      }
    }

    this.updateContent(this.selectedVariant);
  }

  handleFormChange(event) {
    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData(this.form);
    const filteredFormData = Array.from(formData).filter(
      ([key]) => this.optionNames.indexOf(key) >= 0
    );
    const formDataObject = Object.fromEntries(filteredFormData);

    const result = this.variants.find((variant) => {
      return Object.values(formDataObject).every(
        (value, index) => variant[`option${index + 1}`] === value
      );
    });

    filteredFormData.forEach(([key, value]) => {
      const variantTitle = this.querySelector(
        `.variant-list-title-value[data-option-name="${key}"]`
      );
      if (variantTitle) {
        variantTitle.textContent = value;
      }
    });

    this.selectedVariant = result;
    this.updateContent(result);

    // Sync hidden input immediately so the correct variant is submitted
    // even when the save button flow is unavailable (e.g. winter-sale PDP)
    if (this.selectedVariant) {
      document.querySelectorAll(`input[data-input-id="${this.referenceCheckboxId}"]`)
        .forEach((input) => { input.value = this.selectedVariant.id; });
    }
  }

  updateContent(variant) {
    if (!variant) return;

    const productItem = this.closest(".upsell-product-item");
    const searchContext = productItem || this.modalElement;

    if (!searchContext) {
      return;
    }

    const imageSrc =
      (variant.featured_image && variant.featured_image.src) ||
      (variant.featured_media &&
        variant.featured_media.preview_image &&
        variant.featured_media.preview_image.src) ||
      null;

    const upsellMedia = searchContext.querySelector(".image-block .image-wrapper");
    if (upsellMedia && imageSrc) {
      const media = `<img src="${imageSrc}" alt="${variant.title}">`;
      this.replaceMedia(upsellMedia, media, imageSrc);
    }

    const priceList = searchContext.querySelector(".price-list");
    if (priceList) {
      this.replacePriceList(priceList, variant);
    }

    if (productItem) {
      const variantIdInput = productItem.querySelector(
        'input[data-variant-id-input][name="id"]'
      );
      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }
    }
  }

  async handleSaveButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.selectedVariant) {
      const openedFromCart =
        this.modalElement &&
        this.modalElement.getAttribute("data-upsell-source") === "cart";

      const referenceCheckboxes = document.querySelectorAll(
        `input[id="${this.referenceCheckboxId}"]`
      );

      referenceCheckboxes.forEach((referenceCheckbox) => {
        const referenceCheckboxContainer = referenceCheckbox.closest("label");

        if (!referenceCheckboxContainer) return;

        const imageElement =
          referenceCheckboxContainer.querySelector(".upsell-image");
        const selectedImageSrc =
          (this.selectedVariant.featured_image &&
            this.selectedVariant.featured_image.src) ||
          (this.selectedVariant.featured_media &&
            this.selectedVariant.featured_media.preview_image &&
            this.selectedVariant.featured_media.preview_image.src) ||
          null;
        if (imageElement && selectedImageSrc) {
          const media = `
            <img src="${selectedImageSrc}" alt="${this.selectedVariant.title}">
          `;
          this.replaceMedia(
            imageElement,
            media,
            selectedImageSrc
          );
        }

        const priceListElement =
          referenceCheckboxContainer.querySelector(".price-list");
        if (priceListElement) {
          this.replacePriceList(priceListElement, this.selectedVariant);
        }

        // Update variant options in data attribute for bundle summary
        const variantOptions = [
          this.selectedVariant.option1,
          this.selectedVariant.option2,
          this.selectedVariant.option3,
        ]
          .filter((opt) => opt && opt !== "Default Title")
          .join(" / ");

        if (variantOptions) {
          referenceCheckboxContainer.dataset.currentVariantOptions =
            variantOptions;
        }

        // Auto-check the checkbox when variant is selected
        if (!referenceCheckbox.checked) {
          referenceCheckbox.checked = true;
          referenceCheckbox.dispatchEvent(
            new Event("change", { bubbles: true })
          );
        }
      });

      const upsellInputs = document.querySelectorAll(
        `input[data-input-id="${this.referenceCheckboxId}"]`
      );
      upsellInputs.forEach((upsellInput) => {
        upsellInput.value = this.selectedVariant.id;
      });

      if (openedFromCart) {
        if (this.saveButton) this.saveButton.setAttribute("aria-busy", "true");
        await this.addVariantToCart(this.selectedVariant.id);
        if (this.saveButton) this.saveButton.removeAttribute("aria-busy");
      }
    }

    this.closeModal();
  }

  async addVariantToCart(variantId) {
    if (!variantId) return;

    try {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.description || errorData.message || null;
        document.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { message },
          })
        );
        return;
      }

      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      document.dispatchEvent(new CustomEvent("cart:change", { bubbles: true }));
    } catch (error) {
      // Silent fail: do not block modal close.
    }
  }

  replaceMedia(container, media, src) {
    if (!media) return;

    const loadingOverlay = document.createElement("div");
    loadingOverlay.className = "loading-overlay";
    loadingOverlay.innerHTML = `
      <div class="loading-spinner"></div>
    `;
    container.appendChild(loadingOverlay);

    const newImage = new Image();
    newImage.onload = () => {
      container.innerHTML = media;
      loadingOverlay.remove();
    };
    newImage.onerror = () => {
      loadingOverlay.remove();
    };
    newImage.src = src;
  }

  formatMoney(priceInCents) {
    if (!this.moneyFormat || priceInCents === undefined) return "";

    const amount = priceInCents / 100;
    const amountWithDot = amount.toFixed(2);
    const amountWithComma = amountWithDot.replace(".", ",");
    const amountNoDecimals = Math.floor(amount).toString();

    let formattedPrice = this.moneyFormat
      .replace("{{amount_with_comma_separator}}", amountWithComma)
      .replace("{{amount_no_decimals_with_comma_separator}}", amountNoDecimals)
      .replace(
        "{{amount_with_space_separator}}",
        amountWithDot.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      )
      .replace("{{amount_no_decimals}}", amountNoDecimals)
      .replace("{{amount}}", amountWithDot);

    return formattedPrice;
  }

  replacePriceList(priceList, variant) {
    const salePrice = this.formatMoney(variant.price);
    const compareAtPrice = this.formatMoney(variant.compare_at_price);

    priceList.innerHTML = `
        <sale-price class="text-on-sale">${salePrice}</sale-price>
      `;

    if (variant.compare_at_price > variant.price) {
      priceList.innerHTML += `
          <compare-at-price class="line-through text-subdued">${compareAtPrice}</compare-at-price>
        `;
    }
  }

  closeModal() {
    if (!this.modalElement) return;
    this.modalElement.removeAttribute("data-upsell-source");
    const closeButton = this.modalElement.querySelector(
      'button[is="close-button"]'
    );
    if (closeButton) closeButton.click();
  }
}

if (!customElements.get("upsell-variant-selector")) {
  customElements.define("upsell-variant-selector", UpsellVariantSelector);
}
